// src/pages/legal/CookiesPage.js
import React from 'react';
import { useSEO } from '../../lib/useSEO';

const S = {
  page: { maxWidth:760, margin:'0 auto', padding:'48px 24px 80px' },
  h1:   { fontFamily:"'Bebas Neue',sans-serif", fontSize:'clamp(2rem,5vw,2.8rem)', letterSpacing:'.04em', marginBottom:6, color:'#f4f4f4' },
  meta: { fontSize:'.78rem', color:'#444', marginBottom:40 },
  h2:   { fontFamily:"'Barlow Condensed',sans-serif", fontSize:'1.05rem', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'#4cca74', margin:'36px 0 10px', paddingBottom:6, borderBottom:'1px solid rgba(76,202,116,.15)' },
  p:    { color:'#888', fontSize:'.9rem', lineHeight:1.85, marginBottom:14 },
  li:   { color:'#888', fontSize:'.9rem', lineHeight:1.85, marginBottom:8 },
  ul:   { paddingLeft:20, marginBottom:14 },
  b:    { color:'#ccc', fontWeight:600 },
  box:  { background:'rgba(76,202,116,.06)', border:'1px solid rgba(76,202,116,.2)', borderRadius:10, padding:'14px 18px', marginBottom:20 },
  th:   { background:'#111', color:'#4cca74', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', padding:'10px 14px', textAlign:'left', border:'1px solid rgba(255,255,255,.07)', fontSize:'.8rem' },
  td:   { color:'#888', padding:'10px 14px', border:'1px solid rgba(255,255,255,.07)', verticalAlign:'top', lineHeight:1.6, fontSize:'.82rem' },
};

const COOKIES = [
  { name:'cq_age',      type:'Essential',  duration:'Session',  purpose:'Stores age verification result to prevent repeated gate on page refresh.' },
  { name:'cq_lang',     type:'Essential',  duration:'Session',  purpose:'Stores selected language preference.' },
  { name:'cq_country',  type:'Essential',  duration:'Session',  purpose:'Stores selected shipping country.' },
  { name:'cq_cart',     type:'Essential',  duration:'30 days',  purpose:'Persists cart contents between sessions.' },
  { name:'cq_cookie_consent', type:'Essential', duration:'13 months', purpose:'Stores your cookie consent decision.' },
  { name:'sb-*',        type:'Essential',  duration:'Session',  purpose:'Supabase authentication session tokens.' },
  { name:'_ga, _gid',   type:'Analytics',  duration:'26 months / 24 hours', purpose:'Google Analytics — tracks pages visited and session data. Only set with your consent.' },
];

export default function CookiesPage() {
  useSEO({ title:'Cookie Policy', canonical:'/legal/cookies', noIndex:true });
  return (
    <div style={S.page}>
      <p style={{ fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.72rem', fontWeight:700, letterSpacing:'.2em', textTransform:'uppercase', color:'#4cca74', marginBottom:8 }}>Legal</p>
      <h1 style={S.h1}>Cookie Policy</h1>
      <p style={S.meta}>CosmiqHerbz s.r.o. · Last updated: June 2025 · EU ePrivacy Directive compliant</p>

      <h2 style={S.h2}>1. What Are Cookies</h2>
      <p style={S.p}>Cookies are small text files placed on your device when you visit a website. They allow the website to remember your actions and preferences over a period of time, so you do not have to re-enter them each visit. Cookies may be set by the website you are visiting ("first-party cookies") or by third-party services used on that website.</p>

      <h2 style={S.h2}>2. How We Use Cookies</h2>
      <p style={S.p}>We use cookies to:</p>
      <ul style={S.ul}>
        <li style={S.li}>Keep you logged in to your account;</li>
        <li style={S.li}>Remember your age verification, language, and country preferences;</li>
        <li style={S.li}>Maintain your shopping cart between visits;</li>
        <li style={S.li}>Analyse Website traffic and improve our service (with your consent only);</li>
        <li style={S.li}>Record your cookie consent decision.</li>
      </ul>

      <h2 style={S.h2}>3. Cookie Categories</h2>

      <p style={{...S.p, color:'#4cca74', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', fontSize:'.78rem', marginBottom:8}}>Essential Cookies</p>
      <p style={S.p}>These cookies are strictly necessary for the Website to function. They cannot be disabled without breaking core functionality such as login, cart, age verification, and language preferences. No consent is required for essential cookies under EU law.</p>

      <p style={{...S.p, color:'#f0a500', fontFamily:"'Barlow Condensed',sans-serif", fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', fontSize:'.78rem', marginBottom:8}}>Analytics Cookies</p>
      <p style={S.p}>Analytics cookies help us understand how visitors interact with the Website. These cookies are only placed with your explicit consent. If you decline analytics cookies, the Website will continue to function normally.</p>

      <h2 style={S.h2}>4. Cookies We Use</h2>
      <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:20 }}>
        <thead>
          <tr>
            <th style={S.th}>Cookie</th>
            <th style={S.th}>Type</th>
            <th style={S.th}>Duration</th>
            <th style={S.th}>Purpose</th>
          </tr>
        </thead>
        <tbody>
          {COOKIES.map((c,i)=>(
            <tr key={i} style={{background:i%2===0?'rgba(255,255,255,.02)':'none'}}>
              <td style={{...S.td,fontFamily:'monospace',color:'#4cca74',fontSize:'.78rem'}}>{c.name}</td>
              <td style={{...S.td,color:c.type==='Essential'?'#4cca74':'#f0a500',fontWeight:600}}>{c.type}</td>
              <td style={S.td}>{c.duration}</td>
              <td style={S.td}>{c.purpose}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 style={S.h2}>5. Managing Cookies</h2>
      <p style={S.p}>You can manage your cookie preferences at any time using the cookie settings panel, accessible via the "Cookie Settings" link in the website footer. You can also control cookies through your browser settings:</p>
      <ul style={S.ul}>
        <li style={S.li}><b style={S.b}>Chrome:</b> Settings → Privacy and security → Cookies and other site data</li>
        <li style={S.li}><b style={S.b}>Firefox:</b> Settings → Privacy & Security → Cookies and Site Data</li>
        <li style={S.li}><b style={S.b}>Safari:</b> Preferences → Privacy → Manage Website Data</li>
        <li style={S.li}><b style={S.b}>Edge:</b> Settings → Cookies and site permissions</li>
      </ul>
      <p style={S.p}>Blocking essential cookies may prevent the Website from functioning correctly. Blocking analytics cookies will not affect your ability to use the Website.</p>

      <h2 style={S.h2}>6. Third-Party Cookies</h2>
      <p style={S.p}>Our payment service provider may set cookies during the payment process. These are governed by the payment provider's own cookie and privacy policies. We do not control third-party cookies.</p>

      <h2 style={S.h2}>7. Updates</h2>
      <p style={S.p}>We may update this Cookie Policy from time to time. The current version is always available on the Website. Where we add new cookie categories, we will request fresh consent where required.</p>

      <h2 style={S.h2}>8. Contact</h2>
      <div style={S.box}>
        <p style={{...S.p,marginBottom:2}}><b style={S.b}>CosmiqHerbz s.r.o.</b></p>
        <p style={{...S.p,marginBottom:0}}>Cookie queries: <a href="mailto:info@cosmiqherbz.com" style={{color:'#4cca74',textDecoration:'none'}}>info@cosmiqherbz.com</a></p>
      </div>
    </div>
  );
}